import { getCollection, getInventoryConfig } from "@/api/inventory/getConfig";
import { getMetadataOptions } from "@/api/nfts/getMetadata";
import { getNftsInfiniteOptions } from "@/api/nfts/getNFTs";
import { BaseNFTCard } from "@/components/BaseNFTCard";
import { ErrorBoundary } from "@/shared/suspense/ErrorBoundary";
import { LoadingFallback } from "@/shared/suspense/LoadingFallback";
import {
	Affix,
	Button,
	Center,
	Loader,
	Paper,
	Skeleton,
	Stack,
	Text,
	Transition,
	useMantineTheme,
} from "@mantine/core";
import {
	useLocalStorage,
	useViewportSize,
	useWindowScroll,
} from "@mantine/hooks";
import { useAppKitAccount } from "@reown/appkit/react";
import { IconArrowUp } from "@tabler/icons-react";
import {
	useQueryClient,
	useSuspenseInfiniteQuery,
} from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { Suspense, useCallback, useMemo } from "react";

const PAGE_SIZE = 16;

export const Route = createFileRoute("/_auth/$collection/nfts/")({
	loaderDeps: ({ search: { filter } }) => ({ filter }),
	loader: async ({ context, params }) => {
		const collection = await getCollection({
			queryClient: context.queryClient,
			collectionAddress: params.collection,
		});

		return { collection };
	},
	errorComponent: ({ error }) => <ErrorBoundary error={error} />,
	pendingComponent: () => <LoadingFallback />,
	component: NFTGrid,
});

function NFTGrid() {
	const { collection } = Route.useLoaderData();
	const { filter } = Route.useSearch();
	const { address } = useAppKitAccount();
	const { width } = useViewportSize();
	const theme = useMantineTheme();
	const queryClient = useQueryClient();

	const [scroll] = useWindowScroll();

	const [showIncomplete] = useLocalStorage({
		key: "showIncomplete",
		defaultValue: false,
	});

	const getUserAddress = useCallback(() => {
		const inventoryConfig = queryClient.getQueryData(
			getInventoryConfig().queryKey,
		);

		let userAddress: string | undefined;
		switch (filter) {
			case "inventory":
				userAddress = inventoryConfig?.kmsAddresses.inventory;
				break;
			case "vault":
				userAddress = inventoryConfig?.kmsAddresses.minter;
				break;
			case "wallet":
				userAddress = address ?? undefined;
				break;
			case "all":
				userAddress = undefined;
				break;
		}
		return userAddress;
	}, [filter, address, queryClient]);

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useSuspenseInfiniteQuery(
			getNftsInfiniteOptions({
				collections: [collection.address],
				user: getUserAddress(),
				pageSize: PAGE_SIZE,
				includeMetadata: false,
			}),
		);

	let allNfts = useMemo(() => {
		const nfts = data?.pages.flatMap((page) => page.nfts) ?? [];
		return nfts;
	}, [data]);

	if (showIncomplete) {
		allNfts = allNfts.filter((nft) => {
			const metadataQuery = getMetadataOptions(collection.address, nft.tokenId);
			const metadata = queryClient.getQueryData(metadataQuery.queryKey);
			return !metadata || !metadata?.image || !metadata?.name;
		});
	}

	// Calculate responsive column count
	const columnCount = useMemo(() => {
		const smWidth = Number.parseFloat(theme.breakpoints.sm) * 16;
		const mdWidth = Number.parseFloat(theme.breakpoints.md) * 16;
		const lgWidth = Number.parseFloat(theme.breakpoints.lg) * 16;

		if (width < smWidth) return 1;
		if (width < mdWidth) return 2;
		if (width < lgWidth) return 3;
		return 4;
	}, [width, theme.breakpoints]);
	const estimateRowHeight = collection.type === "ERC721A" ? 677 : 436;

	const gridVirtualizer = useWindowVirtualizer({
		count: Math.ceil(allNfts.length / columnCount),
		estimateSize: () => estimateRowHeight,
		overscan: 1,
		horizontal: false,
		gap: 16,
		onChange: (instance) => {
			const lastIndex = instance.getVirtualItems().at(-1)?.index ?? 0;
			const totalRows = Math.ceil(allNfts.length / columnCount);

			if (!isFetchingNextPage && hasNextPage && lastIndex >= totalRows - 3) {
				fetchNextPage();
			}
		},
	});

	if (!data?.pages[0].nfts.length) {
		return (
			<Paper p="md" radius="md">
				<Text>No NFTs found for this collection.</Text>
			</Paper>
		);
	}

	return (
		<>
			<Affix position={{ bottom: 20, right: 20 }}>
				<Transition transition="slide-up" mounted={scroll.y > 100}>
					{(transitionStyles) => (
						<Button
							leftSection={<IconArrowUp size={16} />}
							style={transitionStyles}
							onClick={() => gridVirtualizer.scrollToIndex(0)}
						>
							Scroll to top
						</Button>
					)}
				</Transition>
			</Affix>

			<Stack>
				<Stack
					style={{
						height: `${gridVirtualizer.getTotalSize()}px`,
						width: "100%",
						position: "relative",
					}}
				>
					{gridVirtualizer.getVirtualItems().map((virtualRow) => {
						const rowStartIndex = virtualRow.index * columnCount;
						return (
							<div
								key={virtualRow.index}
								data-index={virtualRow.index}
								ref={gridVirtualizer.measureElement}
								style={{
									position: "absolute",
									top: 0,
									left: 0,
									width: "100%",
									height: `${virtualRow.size}px`,
									transform: `translateY(${virtualRow.start}px)`,
									display: "grid",
									gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
									gap: "16px",
								}}
							>
								{Array.from({ length: columnCount }).map((_, columnIndex) => {
									const nftIndex = rowStartIndex + columnIndex;
									const nft = allNfts[nftIndex];

									if (!nft) return null;

									return (
										<Suspense
											key={nft.tokenId}
											fallback={<Skeleton height={estimateRowHeight} />}
										>
											<Link
												to="/$collection/nfts/$tokenId"
												search={{ balance: nft?.balance }}
												params={{
													collection: collection.address,
													tokenId: nft.tokenId,
												}}
												style={{
													textDecoration: "inherit",
													color: "inherit",
													viewTransitionName: `nft-image-${nft.tokenId}`,
												}}
											>
												<BaseNFTCard
													collectionAddress={collection.address}
													nft={nft}
													style={{ height: virtualRow.size }}
												/>
											</Link>
										</Suspense>
									);
								})}
							</div>
						);
					})}
				</Stack>
				{isFetchingNextPage && (
					<Center pb="md">
						<Loader size="md" />
					</Center>
				)}
			</Stack>
		</>
	);
}
